import { CoreButton, coreToast } from "@verity/components/core";
import { ProctoringOption } from "@verity/proctoring-option/types";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../../../../helpers/api-client";
import { intIdToUuid } from "../../../../../helpers/uuid";
import i18n from "../../../../../i18n";
import { PortalControllerContext } from "../../../../context";
import { useSubmitHandlers } from "../hooks/useSubmitHandlers";
import { ConferencePlatformType, InterviewDetailsType } from "../types";

type SubmitButtonProps = {
	assId?: number;
	assUuid?: string;
	candidateOptions: Omit<InterviewDetailsType & ConferencePlatformType, "startTime" | "endTime" | "interviewerUuids">;
	proctoringOptions: Partial<ProctoringOption>;
	sessionDates: {
		start: InterviewDetailsType["startTime"];
		end: InterviewDetailsType["endTime"];
	};
	interviewerUuids: InterviewDetailsType["interviewerUuids"];
};

export default function EditSubmitButton({
	assId,
	assUuid,
	candidateOptions,
	proctoringOptions,
	sessionDates,
	interviewerUuids,
}: SubmitButtonProps) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lmsProvidedCourseId } = portalContext;

	const [isLoading, setIsLoading] = useState(false);

	const navigate = useNavigate();

	const { handleValidateDates, handleSaveProctoringConfig, handleSaveLiveSession, handleRegisterUsers } =
		useSubmitHandlers({
			candidateOptions,
			proctoringOptions,
			sessionDates,
			interviewerUuids,
			isEdit: true,
		});

	async function onSubmit() {
		try {
			handleValidateDates();
		} catch (err) {
			console.error(err);
			if (err instanceof Error) {
				coreToast.error(err.message);
			}
			return;
		}

		setIsLoading(true);

		let assessmentId: number | undefined = assId || undefined;
		let assessmentUuid = assUuid || crypto.randomUUID().replaceAll("-", "");

		try {
			await handleSaveProctoringConfig(assessmentUuid);

			//If we just saved a new assessment (with a made up id) get the id generated by the db
			if (!assessmentId) {
				const assessment = await apiClient.assessment.getAssessmentByAssessmentInfo({
					courseId: lmsProvidedCourseId,
					quizId: assessmentUuid,
				});
				if (assessment) {
					assessmentId = assessment.id;
					assessmentUuid = assessment.quizId;
				}
			}

			const sessionId = await handleSaveLiveSession(intIdToUuid(assessmentId!));

			await handleRegisterUsers(sessionId);

			setIsLoading(false);
			coreToast.success(
				i18n.t("candidate.scheduleInterview.toast.saveSuccess", { positionName: candidateOptions.positionName }),
			);
			navigate("/interviews/schedule/submitted", {
				state: {
					candidateName: candidateOptions.candidateName,
					positionName: candidateOptions.positionName,
					date: sessionDates.start,
				},
			});
		} catch (e) {
			setIsLoading(false);
			console.error(e);
			coreToast.error(i18n.t("candidate.scheduleInterview.toast.saveFail"));
		}
	}

	return (
		<CoreButton onClick={onSubmit} loading={isLoading}>
			{i18n.t(`candidate.scheduleInterview.navigation.submit`)}
		</CoreButton>
	);
}
